import { Component, OnDestroy, OnInit, Inject, Injector} from '@angular/core';
import { WindowRef } from '@app/@core';
import { NbMenuItem, NbMediaBreakpointsService, NbMenuService, NbSidebarService, NbThemeService, NbWindowService } from '@nebular/theme';
import { AuthService } from '@app/@core-auth';
import { StoreService } from '@app/@graphql/services';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { LayoutService } from '../../../@core/utils';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { filter, tap, map, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subject } from 'rxjs';
import { SETTINGS_TOKEN, Settings } from '@app/settings.config';
import { TranslateService } from '@ngx-translate/core';
import { ChangePasswordComponent } from '@app/auth/containers';
import { RestaurantAdminService } from '@app/@core/services/restaurant-admin.service';
import { CompanyAdminService } from '@app/@core/services/company-admin.service';

enum MenuItemType {
  ChangePassword,
  Logout
}
type MenuItem = NbMenuItem & { id: MenuItemType };

@Component({
    selector: 'ngx-header',
    styleUrls: ['./header.component.scss'],
    templateUrl: './header.component.html',
    standalone: false
})

export class HeaderComponent extends OnDestroyMixin implements OnInit, OnDestroy {

  private destroy$: Subject<void> = new Subject<void>();
  isLoggedIn$: Observable<boolean>;
  userName$: Observable<string>;
  menuItems$: Observable<MenuItem[]>;
  contextMenuTag: string;

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
    {
      value: 'cosmic',
      name: 'Cosmic',
    },
    {
      value: 'corporate',
      name: 'Corporate',
    },
  ];

  currentTheme = 'default';

  displayName: string = '';
  logoUrl: string = '';
  adminService: any;
  isAdminOverride: boolean;

  constructor(private sidebarService: NbSidebarService,
              private menuService: NbMenuService,
              private themeService: NbThemeService,
              private layoutService: LayoutService,
              private breakpointService: NbMediaBreakpointsService,
              private auth: AuthService,
              private windowRef: WindowRef,
              private store: StoreService,
              private nbWindow: NbWindowService,
              private translate: TranslateService,
              private injector : Injector,
              @Inject(SETTINGS_TOKEN) private settings: Settings) 
              
  {
      super();
      this.userName$ = this.auth.selectUserName();
      this.contextMenuTag = this.settings.menusTags.user;
      this.isAdminOverride = this.auth.getIsAdminOverride();

      if (this.auth.getIsRestaurantAdmin()) 
      {
          this.adminService = this.injector.get<RestaurantAdminService>(RestaurantAdminService);
      } 
      else if (this.auth.getIsCompanyAdmin()) 
      {
          this.adminService = this.injector.get<CompanyAdminService>(CompanyAdminService);
      }
  }

  ngOnInit() {
    this.currentTheme = this.themeService.currentTheme;
    this.menuItems$ = this.getMenuItems();

    if (this.adminService) {
      this.adminService.ready()
          .pipe(
              takeUntil(this.destroy$)
          )
          .subscribe(() => {
              this.displayName = this.adminService.DefaultName;
              this.logoUrl = this.adminService.DefaultLogoUrl;
          });
    }

    this.themeService.onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      )
      .subscribe(themeName => this.currentTheme = themeName);

    this.menuService
      .onItemClick()
      .pipe(
        filter(({ tag }) => tag === this.contextMenuTag),
        tap(({ item }) => this.handleMenuItemClick(item as MenuItem)),
        untilComponentDestroyed(this)
      )
      .subscribe();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  changePassword() {
    this.translate
      .get(marker('change_password'))
      .pipe(
        tap(title => this.nbWindow.open(ChangePasswordComponent, { title, windowClass: 'app-window-modal' })),
        untilComponentDestroyed(this)
      )
      .subscribe();
  }

  logout() {
    this.auth.logout();
    this.store.clearStore();
    this.windowRef.nativeWindow.location.href = this.settings.baseHref;
  }

  private getMenuItems(): Observable<MenuItem[]> {
    return forkJoin([this.translate.get(marker('change_password')), this.translate.get(marker('logout'))]).pipe(
      map(([changePassword, logout]) => [
        { title: changePassword, icon: 'shield-outline', id: MenuItemType.ChangePassword },
        { title: logout, icon: 'log-out', id: MenuItemType.Logout }
      ])
    );
  }

  private handleMenuItemClick(item: MenuItem) {
    switch (item.id) {
      case MenuItemType.ChangePassword:
        this.changePassword();
        break;
      case MenuItemType.Logout:
        this.logout();
        break;
    }
  }
}
